import React, { useEffect } from "react";

type Block = { h: string } | { p: string } | { ul: string[] };

const renderInline = (text: string) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-white/90">{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );

const LegalShell = ({ title, updated, blocks }: { title: string; updated: string; blocks: Block[] }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div data-testid="legal-page">
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-3xl px-5 lg:px-10 pt-24 pb-10">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-neon">Legal</div>
          <h1 className="mt-4 font-bebas text-5xl sm:text-6xl tracking-wide uppercase leading-[0.92]" data-testid="legal-title">
            {title}
          </h1>
          <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-white/50">Last updated: {updated}</div>
        </div>
      </div>
      <article className="mx-auto max-w-3xl px-5 lg:px-10 py-16">
        {blocks.map((b, i) => {
          if ("h" in b)
            return (
              <h2 key={i} className="mt-12 first:mt-0 text-2xl font-semibold text-white">
                {b.h}
              </h2>
            );
          if ("ul" in b)
            return (
              <ul key={i} className="mt-5 grid gap-3 pl-5 list-disc marker:text-neon text-base text-white/60 leading-[1.9]">
                {b.ul.map((li, j) => (
                  <li key={j}>{renderInline(li)}</li>
                ))}
              </ul>
            );
          return (
            <p key={i} className="mt-5 whitespace-pre-line text-base text-white/60 leading-[1.9]">
              {renderInline(b.p)}
            </p>
          );
        })}
      </article>
    </div>
  );
};

const UPDATED = "11 August 2026";

export const PrivacyPolicy = () => (
  <LegalShell
    title="Privacy Policy"
    updated={UPDATED}
    blocks={[
      { p: "This Privacy Policy explains how Swapnil Shivhare (\"People's Circle\", \"we\", \"us\", or \"our\") collects, uses, discloses, and protects your information when you use the website https://peoples-circle.com and related services (the \"Platform\"). By using the Platform, you agree to this Policy." },
      { h: "Information we collect" },
      { p: "**Information you provide.** When you sign in with Google, we receive your name, email address, and profile picture from your Google account. When you register for events, sign petitions, post discussions or blogs, apply for fellowships, or contact us, we collect the information you submit." },
      { p: "**Payment information.** When you buy a paid event ticket, payment is processed by our payment partner, Razorpay. We do not collect or store your full card, UPI, or bank details on our servers. Razorpay processes payment data under its own privacy policy and applicable PCI-DSS standards. We retain limited transaction records (such as order ID, amount, and status) for accounting and support." },
      { p: "**Automatically collected information.** We collect standard technical data such as IP address, browser type, device information, and usage activity to operate and secure the Platform." },
      { h: "How we use your information" },
      { p: "We use your information to: create and manage your account; process event registrations and ticket payments; deliver tickets, confirmations, and reminder emails; enable community features (blogs, petitions, discussions, spotlights, fellowships); respond to your requests; maintain security and prevent fraud; and comply with legal obligations." },
      { h: "How we share your information" },
      { p: "We share information only as needed with: service providers who help us operate the Platform, including Razorpay (payments), our email provider (transactional emails such as tickets and reminders), our cloud database and file-storage providers, and authentication via Google; event organisers, limited to the attendee information necessary to manage an event you register for; and authorities, where required by law. We do not sell your personal information." },
      { h: "Cookies" },
      { p: "We use cookies and similar technologies to keep you signed in, remember preferences, and understand usage. You can control cookies through your browser settings; disabling them may affect Platform functionality." },
      { h: "Data retention and security" },
      { p: "We retain personal information for as long as your account is active or as needed to provide services and meet legal, accounting, and reporting requirements. We use reasonable technical and organisational measures to protect your data, though no method of transmission or storage is completely secure." },
      { h: "Your rights" },
      { p: "You may access, correct, or request deletion of your personal information, and withdraw consent, by contacting us at admin@peoples-circle.com. We will respond in accordance with applicable law." },
      { h: "Children" },
      { p: "The Platform is not intended for children under 18. We do not knowingly collect information from minors." },
      { h: "Changes" },
      { p: "We may update this Policy from time to time. Material changes will be posted on this page with a new \"Last updated\" date." },
      { h: "Grievance Officer" },
      { p: "In accordance with the Information Technology Act, 2000 and rules thereunder, the Grievance Officer is:" },
      { ul: ["Name: Swapnil Shivhare", "Email: admin@peoples-circle.com", "Address: F35, Runwal Galleria, Dombivli East, Thane, 421204, Maharashtra"] },
    ]}
  />
);

export const Terms = () => (
  <LegalShell
    title="Terms & Conditions"
    updated={UPDATED}
    blocks={[
      { p: "These Terms & Conditions (\"Terms\") govern your use of https://peoples-circle.com and related services (the \"Platform\"), operated by Swapnil Shivhare, a sole proprietorship having its registered office at F35, Runwal Galleria, Dombivli East, Thane, 421204, Maharashtra. By accessing or using the Platform, you agree to these Terms. If you do not agree, do not use the Platform." },
      { h: "Eligibility and accounts" },
      { p: "You must be at least 18 years old to use the Platform. You are responsible for the activity under your account and for keeping your login secure. You agree to provide accurate information and to keep it up to date." },
      { h: "The service" },
      { p: "People's Circle is an online community platform that allows users to discover and register for events (free and paid), read and publish blogs, create and sign petitions, participate in discussions, view member spotlights, and apply for fellowships. Some events require a paid ticket." },
      { h: "User content and conduct" },
      { p: "You retain ownership of content you post, and you grant us a non-exclusive licence to host and display it on the Platform. You agree not to post content that is unlawful, hateful, harassing, infringing, misleading, or otherwise objectionable, and not to misuse, disrupt, or attempt to gain unauthorised access to the Platform. We may remove content or suspend accounts that violate these Terms." },
      { h: "Events and tickets" },
      { p: "Events may be organised by us or by community organisers. Ticket prices and event details are shown on each event page. When you purchase a paid ticket, you agree to the price displayed and to our Cancellation & Refunds Policy. We are not responsible for the conduct of third-party organisers or attendees, though we act to keep the community safe." },
      { h: "Payments" },
      { p: "Paid tickets are processed through Razorpay. By making a payment you also agree to Razorpay's terms. You represent that you are authorised to use the payment method provided." },
      { h: "Intellectual property" },
      { p: "The Platform, including its design, logos, and software, is owned by Swapnil Shivhare and protected by applicable laws. You may not copy, modify, or distribute it without permission." },
      { h: "Disclaimers and limitation of liability" },
      { p: "The Platform is provided \"as is\" without warranties of any kind. To the maximum extent permitted by law, Swapnil Shivhare is not liable for any indirect, incidental, or consequential damages arising from your use of the Platform. Our total liability for any claim relating to a paid ticket is limited to the amount you paid for that ticket." },
      { h: "Indemnity" },
      { p: "You agree to indemnify and hold harmless Swapnil Shivhare from claims arising out of your use of the Platform or your violation of these Terms." },
      { h: "Termination" },
      { p: "We may suspend or terminate your access at any time for violation of these Terms or misuse of the Platform." },
      { h: "Governing law" },
      { p: "These Terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts at Thane, Maharashtra." },
      { h: "Contact" },
      { p: "Questions about these Terms: admin@peoples-circle.com." },
    ]}
  />
);

export const Refunds = () => (
  <LegalShell
    title="Cancellation & Refunds"
    updated={UPDATED}
    blocks={[
      { p: "This policy applies to paid event tickets purchased on https://peoples-circle.com through Razorpay. Free events involve no payment and can be cancelled by the attendee at any time at no cost." },
      { h: "Cancellation by the organiser" },
      { p: "If an event is cancelled by us or the organiser, you will receive a full refund of the ticket price after applicable taxes and transaction charges. If an event is rescheduled, your ticket remains valid for the new date; if you cannot attend the new date, you may request a full refund." },
      { h: "Cancellation by the attendee" },
      { ul: [
        "Cancellations made within 24 hours of booking: eligible for a refund of the ticket price, less any payment-gateway charges.",
        "Cancellations made after 24 hours of booking: not eligible for a refund of the ticket price.",
        "No-shows (failure to attend without prior cancellation): not eligible for a refund.",
      ] },
      { h: "How to request a refund" },
      { p: "Email admin@peoples-circle.com from the address you used to register, with your order ID and event name. Eligible refunds are processed to your original payment method via Razorpay, typically within 5-7 business days after approval. The time for the amount to reflect in your account depends on your bank or payment provider." },
      { h: "Duplicate or failed payments" },
      { p: "If you are charged more than once for the same ticket, or money is debited for a failed transaction, contact admin@peoples-circle.com and we will verify and refund any duplicate or erroneous charge." },
      { h: "Contact" },
      { p: "For any cancellation or refund query: admin@peoples-circle.com, 8287025260." },
    ]}
  />
);

export const Shipping = () => (
  <LegalShell
    title="Shipping & Delivery"
    updated={UPDATED}
    blocks={[
      { p: "People's Circle sells digital event tickets only. There are no physical products and no physical shipping." },
      { h: "How tickets are delivered" },
      { p: "After a successful payment through Razorpay, your ticket is generated and delivered electronically:" },
      { ul: [
        "A confirmation email with your PDF ticket is sent to the email address associated with your account, usually immediately and in any case within a few minutes of successful payment.",
        "Your registration and ticket are also available within your account on the Platform.",
      ] },
      { h: "If you do not receive your ticket" },
      { p: "If your payment succeeded but you did not receive your ticket email within 30 minutes, please check your spam or promotions folder, then contact admin@peoples-circle.com with your order ID. We will re-send your ticket promptly." },
      { h: "Contact" },
      { p: "admin@peoples-circle.com, 8287025260." },
    ]}
  />
);

export const Pricing = () => (
  <LegalShell
    title="Pricing"
    updated={UPDATED}
    blocks={[
      { h: "Membership" },
      { p: "Creating an account and joining the People's Circle community is free. Browsing events, blogs, petitions, discussions, and spotlights is free." },
      { h: "Event tickets" },
      { p: "Some events require a paid ticket. For every paid event:" },
      { ul: [
        "The ticket price is displayed clearly on that event's page before you complete registration.",
        "All prices are listed in Indian Rupees (INR).",
        "The price shown is the amount payable; any applicable taxes or fees, if charged, are indicated at checkout.",
      ] },
      { h: "Payments" },
      { p: "Payments are collected securely through Razorpay, which supports cards, UPI, net banking, and wallets. You will see the final payable amount on the Razorpay checkout before confirming." },
      { h: "Contact" },
      { p: "For questions about pricing or a specific event: admin@peoples-circle.com." },
    ]}
  />
);

export const Contact = () => (
  <LegalShell
    title="Contact Us"
    updated={UPDATED}
    blocks={[
      { p: "We would love to hear from you." },
      { p: "**Swapnil Shivhare** (People's Circle)" },
      { ul: [
        "Email: admin@peoples-circle.com",
        "Phone: 8287025260",
        "Address: F35, Runwal Galleria, Dombivli East, Thane, 421204, Maharashtra",
      ] },
      { p: "For support with events, tickets, refunds, or your account, email admin@peoples-circle.com and we will respond as soon as possible, typically within 1-2 business days." },
    ]}
  />
);
